"use client";

import { useMemo } from "react";
import styles from "./Form.module.css";

/**
 * PasswordStrengthMeter — pasek siły hasła + lista wymagań z live-update.
 *
 * **Single source of truth = backend.** Reguły i regex pochodzą z payloadu
 * `password_requirements` (z `IPASSWORD_RULES::toApiResponse()` po stronie
 * PHP) przekazanego jako prop `requirements`. Komponent NIE hardkoduje
 * własnych reguł — zmiana wymagań w PHP `IPASSWORD_RULES` automatycznie
 * propaguje do paska i listy.
 *
 * **Co renderuje:**
 *   1. Pasek z N segmentami (1 segment = 1 aktywna reguła). Kolor zmienia
 *      się progresywnie: czerwony -> pomarańczowy -> zielony.
 *   2. Etykieta poziomu siły — pochodzi z props `labels.levels[score]`.
 *   3. Lista wymagań — każde z ikoną check (✓ zielony, met) lub circle
 *      (○ szary, unmet) — live update przy każdym keystroku.
 *
 * **Akceptuje hasło puste** — wtedy wszystkie reguły "unmet" + pasek pusty +
 * brak etykiety. Komponent zawsze zajmuje stałą wysokość żeby formularz
 * nie skakał przy wpisywaniu.
 *
 * **NIE jest źródłem walidacji** — tylko wizualizacja. Schema zod w form
 * (buildRegisterSchema/buildResetPasswordSchema) używa `buildPasswordRegex()`
 * helper'a (export poniżej) zbudowanego z TEGO SAMEGO payloadu — spójność
 * gwarantowana bo oba pochodzą z `requirements`.
 *
 * **Reusable:** używaj w każdej formie ustalającej/zmieniającej hasło
 * (rejestracja, reset, change-password, profile). Wszystkie formy
 * konsumują `password_requirements` z payloadu komponentu CMS.
 */
export function PasswordStrengthMeter({
  password,
  requirements,
  labels,
}: PasswordStrengthMeterProps) {
  /* Dynamicznie buduj listę aktywnych reguł z payloadu — jeśli backend
     wyłączy np. `requires_uppercase`, ta reguła nie pojawi się ani na
     liście ani w score. */
  const activeRules = useMemo(() => buildActiveRules(requirements), [requirements]);

  const { passed, score, maxScore } = useMemo(() => {
    const pwd = password ?? "";
    const passed = activeRules.map((r) => r.test(pwd));
    return {
      passed,
      score: passed.filter(Boolean).length,
      maxScore: activeRules.length,
    };
  }, [password, activeRules]);

  const hasInput = (password ?? "").length > 0;
  const levelKey: LevelKey | null = hasInput ? scoreToLevelKey(score, maxScore) : null;
  /* Color level (1-4) — niezależne od liczby aktywnych reguł. Mapujemy
     score/maxScore na [1..4] żeby zachować spójną paletę kolorów paska
     niezależnie od konfiguracji backendu (3 reguły lub 5 reguł wciąż
     pokażą czerwony->zielony progres). */
  const colorLevel = hasInput
    ? Math.max(1, Math.min(4, Math.ceil((score / maxScore) * 4)))
    : 0;

  return (
    <div className={styles.passwordStrength} aria-live="polite">
      <div className={styles.strengthBar} aria-hidden="true">
        {activeRules.map((_, i) => (
          <span
            key={i}
            className={`${styles.strengthBarSegment} ${
              i < score
                ? styles[`strengthBarLevel${colorLevel}` as const] ?? ""
                : ""
            }`}
          />
        ))}
      </div>

      {levelKey && (
        <div
          className={`${styles.strengthLabel} ${
            styles[`strengthLabel${colorLevel}` as const] ?? ""
          }`}
        >
          {labels.strengthLabel}: {labels.levels[levelKey]}
        </div>
      )}

      <ul className={styles.strengthRules}>
        {activeRules.map((rule, i) => (
          <li
            key={rule.key}
            className={`${styles.strengthRule} ${
              passed[i] ? styles.strengthRuleMet : styles.strengthRuleUnmet
            }`}
          >
            <span className={styles.strengthRuleIcon} aria-hidden="true" />
            <span>{labels.requirements[rule.key]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── Dynamic rules builder (z PasswordRequirements payload) ──────────── */

type RuleKey = "length" | "lowercase" | "uppercase" | "digit" | "special";

type Rule = {
  key: RuleKey;
  test: (password: string) => boolean;
};

/** Buduje listę reguł z payloadu PHP. Length zawsze pierwsza, reszta opcjonalna
 *  zależnie od `requires_*` flags. */
function buildActiveRules(requirements: PasswordRequirementsInput): Rule[] {
  const rules: Rule[] = [];

  rules.push({
    key: "length",
    test: (p) =>
      p.length >= requirements.min_length && p.length <= requirements.max_length,
  });

  if (requirements.requires_lowercase) {
    rules.push({ key: "lowercase", test: (p) => /[a-z]/.test(p) });
  }
  if (requirements.requires_uppercase) {
    rules.push({ key: "uppercase", test: (p) => /[A-Z]/.test(p) });
  }
  if (requirements.requires_digit) {
    rules.push({ key: "digit", test: (p) => /\d/.test(p) });
  }
  if (requirements.requires_special) {
    rules.push({ key: "special", test: (p) => /[^a-zA-Z0-9]/.test(p) });
  }

  return rules;
}

/* ─── Public helper: buduje JS RegExp z payloadu ─────────────────────── */

/**
 * Buduje JS `RegExp` z PCRE pattern w `requirements.regex` (PHP wysyła bez
 * delimiterów). Używaj w zod schemas:
 *
 * ```ts
 * pass: z.string().regex(buildPasswordRegex(req), msg)
 * ```
 *
 * Backend zwraca pattern body (`((?=.*\d)...{8,20})`); my anchor'ujemy
 * `^...$` żeby zod wymusił match całego stringa (bez tego `pass1234ABC`
 * przeszłoby ale i tak validate jako "contains valid substring").
 */
export function buildPasswordRegex(requirements: PasswordRequirementsInput): RegExp {
  return new RegExp("^" + requirements.regex + "$");
}

/* ─── Score -> level key ─────────────────────────────────────────────────── */

type LevelKey = "veryWeak" | "weak" | "fair" | "good";

function scoreToLevelKey(score: number, maxScore: number): LevelKey {
  /* Normalize do skali 0-1, mapuj na 4 poziomy. Działa niezależnie od
     liczby aktywnych reguł — gdy backend skonfiguruje 3 lub 5 reguł, ratio
     pozostaje sensowne. */
  if (maxScore === 0) return "veryWeak";
  const ratio = score / maxScore;
  if (ratio <= 0.25) return "veryWeak";
  if (ratio <= 0.5) return "weak";
  if (ratio < 1) return "fair";
  return "good";
}

/* ─── Props ────────────────────────────────────────────────────────────── */

/** Minimalny shape — primitive nie zależy od pełnego `PasswordRequirements`
 *  z cms-catalog (decoupling: caller może mieć własny source). */
export type PasswordRequirementsInput = {
  regex: string;
  min_length: number;
  max_length: number;
  requires_lowercase: boolean;
  requires_uppercase: boolean;
  requires_digit: boolean;
  requires_special: boolean;
};

export type PasswordStrengthMeterProps = {
  /** Aktualna wartość hasła z form state (np. `watch("pass")`). */
  password: string;
  /** Payload z backendu — single source of truth dla regex + flag reguł. */
  requirements: PasswordRequirementsInput;
  /** Tłumaczenia — caller podaje slice z `.i18n` dla bieżącego locale. */
  labels: PasswordStrengthLabels;
};

export type PasswordStrengthLabels = {
  /** Etykieta przed nazwą poziomu (np. "Siła hasła"). */
  strengthLabel: string;
  /** Nazwy poziomów per liczba spełnionych reguł (proporcjonalnie). */
  levels: Record<LevelKey, string>;
  /** Etykiety per regułę — gotowe stringi po interpolacji (np. `length`
   *  już z podstawionymi min/max). Interpolacja po stronie caller'a
   *  (helper `buildPasswordStrengthLabels`) — primitive nie zna tłumaczeń. */
  requirements: Record<RuleKey, string>;
};
